import { Fragment, useCallback, type FunctionComponent, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useHistory } from 'react-router'
import { Link } from 'react-router-dom'
import { ArrowLeftIcon } from '@instacart/ids-core'
import { TextTitleRow, useTheme } from '@retailer-platform/shared-components/src/tds'
import { getSettingsBackHref } from './settingsBackHrefStore'
import { useIsNavItemActive, useResolveNavHref } from './TdsNavigationBar'
import { useIsOnSettingsRoute } from './useIsOnSettingsRoute'
import { type NavSettingsV3Item, useNavHierarchySettingsV3 } from './useNavHierarchySettingsV3'
import { NavBadgeIndicator } from './nav-badges/NavBadgeIndicator'
import { useNavBadges } from './nav-badges/useNavBadges'

const SIDEBAR_WIDTH = 273

// Section label above each group of rows. Per Figma 11332:43143: 12px semibold,
// dark grayscale (not low-contrast), with generous top padding to space sections.
const SettingsSectionHeader: FunctionComponent<{ title: string }> = ({ title }) => {
  const theme = useTheme()
  return (
    <div
      css={{
        padding: '24px 16px 4px 32px',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: '18px',
        color: theme.colors.systemGrayscale80,
        cursor: 'default',
      }}
    >
      {title}
    </div>
  )
}

// One row in the settings sidebar. Self-resolves its href and active state via
// the same hooks the primary v3 nav uses, so highlighting stays consistent.
// Renders as a react-router <Link> for SPA-internal routes (preserves modifier-
// key behavior), as a plain <a target=_blank> for external hrefs, and as an
// inert <div> for placeholder items that don't have a destination yet.
const SettingsNavRow: FunctionComponent<{ item: NavSettingsV3Item }> = ({ item }) => {
  const theme = useTheme()
  const resolved = useResolveNavHref(item)
  const isActive = useIsNavItemActive(item)
  const badges = useNavBadges(item)

  const baseStyles = {
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
    padding: '6px 28px 6px 32px',
    backgroundColor: isActive ? theme.colors.brandPrimaryExtraLight : 'transparent',
    color: isActive ? theme.colors.brandPrimaryDark : theme.colors.brandPrimaryRegular,
    boxShadow: isActive ? `inset 4px 0 0 ${theme.colors.brandPrimaryDark}` : undefined,
    fontSize: 14,
    fontWeight: isActive ? 600 : 400,
    lineHeight: '20px',
    textDecoration: 'none',
  } as const

  const interactiveStyles = {
    ...baseStyles,
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.colors.brandPrimaryLight },
  } as const

  const label: ReactNode = (
    <>
      <FormattedMessage id={item.name} />
      <NavBadgeIndicator badges={badges} />
    </>
  )

  if (!resolved?.href) {
    return (
      <div
        css={{ ...baseStyles, color: theme.colors.systemGrayscale30, cursor: 'default' }}
        aria-disabled="true"
      >
        {label}
      </div>
    )
  }

  if (resolved.isExternal) {
    return (
      <a href={resolved.href} target="_blank" rel="noreferrer" css={interactiveStyles}>
        {label}
      </a>
    )
  }

  return (
    <Link to={resolved.href} css={interactiveStyles}>
      {label}
    </Link>
  )
}

type Props = {
  // Route name to fall back to when the user clicks Back without an entry-page
  // captured in the module-scoped store (e.g. cold-loaded directly into a
  // settings URL). Resolved via the same useResolveNavHref hook used by NavItems.
  homeRoute: string
}

export const TdsSettingsSidebar: FunctionComponent<Props> = ({ homeRoute }) => {
  const intl = useIntl()
  const theme = useTheme()
  const history = useHistory()
  const { sections } = useNavHierarchySettingsV3()
  const isOnSettingsRoute = useIsOnSettingsRoute()
  const homeHref = useResolveNavHref({ route: homeRoute })?.href

  const handleBackClick = useCallback(() => {
    const stored = getSettingsBackHref()
    const target = stored ?? homeHref
    if (target) history.push(target)
  }, [history, homeHref])

  // Hide on every route that isn't part of the settings hierarchy. This is the
  // single switch that makes the sidebar conditional — the component is mounted
  // unconditionally in the v3 shell and self-hides here.
  if (!isOnSettingsRoute) {
    return null
  }

  return (
    <aside
      aria-label={intl.formatMessage({ id: 'settings.title' })}
      css={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        height: '100%',
        overflowY: 'auto',
        borderRight: `1px solid ${theme.colors.systemGrayscale20}`,
        borderBottom: `1px solid ${theme.colors.systemGrayscale20}`,
        backgroundColor: theme.colors.systemGrayscale00,
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 24,
      }}
    >
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          paddingLeft: 32,
          paddingTop: 8,
        }}
      >
        <button
          type="button"
          onClick={handleBackClick}
          css={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: 0,
            background: 'none',
            border: 'none',
            color: theme.colors.brandPrimaryRegular,
            cursor: 'pointer',
            font: 'inherit',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '20px',
            textAlign: 'left',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <ArrowLeftIcon size={16} fill="currentColor" />
          <FormattedMessage id="navV3.settings.back" />
        </button>
        <TextTitleRow
          subtitle={intl.formatMessage({ id: 'settings.title' })}
          subtitleTypography="subtitle"
        />
      </div>
      <nav aria-label={intl.formatMessage({ id: 'settings.title' })}>
        {sections.map((section, sIdx) => (
          <Fragment key={`${section.name}-${sIdx}`}>
            <SettingsSectionHeader title={intl.formatMessage({ id: section.name })} />
            {section.items.map((item, iIdx) => (
              <SettingsNavRow key={`${item.name}-${iIdx}`} item={item} />
            ))}
          </Fragment>
        ))}
      </nav>
    </aside>
  )
}
